/**
 * Contracts list.
 */
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/page-header";
import { ContractTypePill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { contractsApi, type Contract } from "@/lib/api-cadastros";
import { formatCurrency, formatDate } from "@/lib/format";

export function ContractsPage() {
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ["contracts"], queryFn: () => contractsApi.list({ size: 200 }) });

  const columns: ColumnDef<Contract>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: "Contrato",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      id: "type",
      accessorKey: "type",
      header: "Tipo",
      cell: ({ row }) => <ContractTypePill type={row.original.type} />,
    },
    {
      id: "starts_at",
      accessorKey: "starts_at",
      header: "Início",
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.starts_at)}</span>,
    },
    {
      id: "ends_at",
      accessorKey: "ends_at",
      header: "Fim",
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.ends_at)}</span>,
    },
    {
      id: "monthly_credit_limit",
      accessorKey: "monthly_credit_limit",
      header: "Crédito mensal",
      cell: ({ row }) =>
        row.original.monthly_credit_limit ? (
          <span className="font-semibold tabular-nums">
            {formatCurrency(row.original.monthly_credit_limit)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "sla_hours",
      accessorKey: "sla_hours",
      header: "SLA",
      cell: ({ row }) =>
        row.original.sla_hours ? (
          <Badge variant="info">{row.original.sla_hours}h</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "active",
      accessorKey: "active",
      header: "Status",
      cell: ({ row }) =>
        row.original.active ? (
          <Badge variant="success" dot>Ativo</Badge>
        ) : (
          <Badge variant="muted" dot>Inativo</Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Contratos"
        description="Contratos comerciais ativos com clientes."
      />
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar contrato..."
        emptyTitle="Nenhum contrato cadastrado"
        onRowClick={(c) => navigate(`/contracts/${c.id}`)}
        columnLabels={{
          name: "Contrato",
          type: "Tipo",
          starts_at: "Início",
          ends_at: "Fim",
          monthly_credit_limit: "Crédito",
          sla_hours: "SLA",
          active: "Status",
        }}
      />
    </>
  );
}
